import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';

export function Hero() {
  return (
    <section
      id="top"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 pb-16 pt-28 text-center md:pt-32"
    >
      <div className="pointer-events-none absolute left-0 top-0 h-full w-full overflow-hidden">
        <div className="absolute left-1/2 top-[20%] h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-primary/[0.05] blur-[150px]" />
        <div className="absolute right-[10%] top-[40%] h-[300px] w-[300px] rounded-full bg-blue-900/[0.08] blur-[100px]" />
        <div className="absolute bottom-0 left-0 h-1/2 w-full bg-gradient-to-t from-[#060B16] to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative mb-10 flex h-20 w-20 items-center justify-center md:h-24 md:w-24"
        >
          <div className="absolute inset-[-20px] animate-[spin_20s_linear_infinite] rounded-full border border-primary/20 motion-reduce:animate-none md:inset-[-24px]">
            <div className="absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_10px_rgba(212,175,55,0.8)]" />
          </div>
          <div className="absolute inset-[-42px] animate-[spin_35s_linear_infinite_reverse] rounded-full border border-white/[0.04] motion-reduce:animate-none md:inset-[-50px]">
            <div className="absolute bottom-0 left-1/2 h-1 w-1 -translate-x-1/2 translate-y-1/2 rounded-full bg-slate-400/50 shadow-[0_0_6px_rgba(148,163,184,0.4)]" />
          </div>
          <div className="absolute inset-[-16px] rounded-full bg-primary/[0.06] blur-xl" />
          <img
            src="/logo_visep_192.webp"
            alt="VISEP"
            className="relative z-10 h-20 w-20 drop-shadow-[0_0_24px_rgba(212,175,55,0.25)] md:h-24 md:w-24"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 flex items-center gap-2 rounded-full border border-primary/20 bg-primary/[0.05] px-4 py-2 backdrop-blur-sm"
        >
          <span className="h-2 w-2 animate-[pulse-glow_2s_ease-in-out_infinite] rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)] motion-reduce:animate-none" />
          <span className="font-mono text-xs uppercase tracking-widest text-primary">
            Seguridad Privada — Paraguay
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-8 text-5xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-8xl"
        >
          Su Seguridad,
          <br />
          <span className="bg-gradient-to-r from-primary via-[#F5D76E] to-primary bg-clip-text text-transparent">
            Nuestra Misión.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mx-auto mb-14 max-w-2xl text-lg leading-relaxed text-slate-400 md:text-xl"
        >
          Protegiendo lo que más importa en Paraguay con soluciones de seguridad integrales,
          personal de élite y tecnología de vanguardia.
        </motion.p>

        <motion.a
          href="#quotation"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="group relative cursor-pointer"
        >
          <div className="absolute -inset-[2px] overflow-hidden rounded-full">
            <div
              className="absolute inset-[-50%] animate-[spinSlow_3s_linear_infinite] motion-reduce:animate-none"
              style={{
                background:
                  'conic-gradient(from 0deg, transparent 0%, transparent 30%, #D4AF37 42%, #F5D76E 50%, #D4AF37 58%, transparent 70%, transparent 100%)',
              }}
            />
          </div>
          <div className="absolute inset-0 rounded-full bg-[#0A0A0A]" />
          <div className="absolute inset-0 scale-125 rounded-full bg-primary/20 opacity-0 blur-2xl transition-opacity duration-700 group-hover:opacity-100" />
          <div className="relative z-10 flex h-14 items-center justify-center gap-3 overflow-hidden rounded-full px-12 transition-all duration-500">
            <div
              className="absolute inset-0 animate-[shimmerSweep_2.5s_ease-in-out_infinite] opacity-0 transition-opacity duration-500 group-hover:opacity-100 motion-reduce:animate-none"
              style={{
                background:
                  'linear-gradient(105deg, transparent 35%, rgba(212,175,55,0.06) 42%, rgba(245,215,110,0.12) 50%, rgba(212,175,55,0.06) 58%, transparent 65%)',
                backgroundSize: '200% 100%',
              }}
            />
            <div className="absolute left-[15%] right-[15%] top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            <span className="relative text-base font-semibold tracking-wide text-primary transition-colors duration-500 group-hover:text-[#F5D76E]">
              Solicitar Cotización
            </span>
            <div className="relative h-5 w-5 overflow-hidden">
              <ArrowRight
                className="h-5 w-5 text-primary transition-all duration-500 group-hover:translate-x-6 group-hover:text-[#F5D76E]"
                aria-hidden
              />
              <ArrowRight
                className="absolute -left-6 top-0 h-5 w-5 text-[#F5D76E] transition-all duration-500 group-hover:translate-x-6"
                aria-hidden
              />
            </div>
          </div>
        </motion.a>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce text-slate-500 motion-reduce:animate-none"
      >
        <ChevronDown className="h-8 w-8" aria-hidden />
      </motion.div>
    </section>
  );
}
